import React, { useState, useEffect } from 'react';
import { pixelAPI, accountAPI } from '../services/api';
import { Activity, Eye, ShoppingCart, MousePointer, Copy, Check, Filter, Code } from 'lucide-react';
import Pagination from '../components/Pagination';
import toast from 'react-hot-toast';

const PER_PAGE = 30;

const EVENT_LABELS = {
  page_view: { label: 'Просмотр страницы', icon: '📄', cls: 'badge-info' },
  product_view: { label: 'Просмотр товара', icon: '👁', cls: 'badge-info' },
  add_to_cart: { label: 'Добавление в корзину', icon: '🛒', cls: 'badge-success' },
  remove_from_cart: { label: 'Удаление из корзины', icon: '❌', cls: 'badge-danger' },
  cart_abandon: { label: 'Брошенная корзина', icon: '🚪', cls: 'badge-warning' },
  purchase: { label: 'Покупка', icon: '💰', cls: 'badge-success' },
  registration: { label: 'Регистрация', icon: '👤', cls: 'badge-info' },
  email_subscribe: { label: 'Подписка email', icon: '📧', cls: 'badge-info' },
};

const EVENT_TYPES = ['', 'page_view', 'product_view', 'add_to_cart', 'remove_from_cart', 'cart_abandon', 'purchase', 'registration', 'email_subscribe'];

export default function PixelPage() {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState('');
  const [days, setDays] = useState(30);
  const [pixelId, setPixelId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadStats = () => {
    pixelAPI.stats(days).then(res => setStats(res.data)).catch(() => {});
  };

  const loadEvents = (p = page) => {
    pixelAPI.events((p - 1) * PER_PAGE, PER_PAGE, filterType || null, days)
      .then(res => { setEvents(res.data.items); setTotal(res.data.total); })
      .catch(() => {});
  };

  useEffect(() => {
    Promise.all([
      pixelAPI.stats(days),
      pixelAPI.events(0, PER_PAGE, null, days),
      accountAPI.getPixelId(),
    ]).then(([sRes, eRes, aRes]) => {
      setStats(sRes.data);
      setEvents(eRes.data.items);
      setTotal(eRes.data.total);
      setPixelId(aRes.data.pixel_id || '');
      setApiKey(aRes.data.api_key || '');
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadEvents(page); }, [page]);
  useEffect(() => { loadStats(); loadEvents(1); setPage(1); }, [filterType, days]);

  const handleCopy = () => {
    const code = `<script src="https://segmira.ru/pixel/segmira.js" data-pixel="${pixelId}"></script>`;
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success('Код скопирован');
    setTimeout(() => setCopied(false), 3000);
  };

  const getEventLabel = (type) => {
    const e = EVENT_LABELS[type];
    return e ? <span className={`badge ${e.cls}`}>{e.icon} {e.label}</span> : <span className="badge badge-info">{type}</span>;
  };

  if (loading) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Загрузка...</div>;

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Веб-пиксель</h1>
        <p className="page-subtitle">Трекинг событий на сайте</p>
      </div>


      <div className="card" style={{ marginBottom: 24, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <Activity size={20} style={{ color: 'var(--accent)' }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>Код для установки на сайт</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Вставьте перед закрывающим тегом {'</body>'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <code style={{
            flex: 1, padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)', fontSize: 12, fontFamily: 'var(--font-mono)',
            color: 'var(--text-primary)', overflowX: 'auto', whiteSpace: 'nowrap',
          }}>
            {`<script src="https://segmira.ru/pixel/segmira.js" data-pixel="${pixelId}"></script>`}
          </code>
          <button className="btn btn-ghost btn-sm" onClick={handleCopy} title="Скопировать">
            {copied ? <Check size={16} style={{ color: 'var(--success)' }} /> : <Copy size={16} />}
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
          Pixel ID: <strong style={{ fontFamily: 'var(--font-mono)' }}>{pixelId}</strong>
        </div>
      </div>


      <div className="card" style={{ marginBottom: 24, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <Code size={20} style={{ color: 'var(--success)' }} />
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>API-ключ для интеграции</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Используйте в заголовке <code>X-Token</code> для внешнего API</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <code style={{ flex: 1, padding: '10px 14px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 12, fontFamily: 'var(--font-mono)', overflowX: 'auto', whiteSpace: 'nowrap' }}>
            {apiKey || 'Не сгенерирован'}
          </code>
          <button className="btn btn-ghost btn-sm" onClick={() => {
            navigator.clipboard.writeText(apiKey);
            setApiKeyCopied(true);
            toast.success('API-ключ скопирован');
            setTimeout(() => setApiKeyCopied(false), 3000);
          }}>
            {apiKeyCopied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10, lineHeight: 1.6 }}>
          Базовый URL: <code>https://segmira.ru/api/ext/</code><br/>
          Документация: <code>/docs</code> → раздел «external»
        </div>
      </div>


      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
          <div className="stat-card">
            <div className="stat-card-label">Событий</div>
            <div className="stat-card-value">{stats.total_events.toLocaleString('ru')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Посетителей</div>
            <div className="stat-card-value">{stats.unique_visitors.toLocaleString('ru')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Сессий</div>
            <div className="stat-card-value">{stats.unique_sessions.toLocaleString('ru')}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-label">Брош. корзин</div>
            <div className="stat-card-value" style={{ color: 'var(--warning)' }}>{stats.abandoned_carts}</div>
          </div>
        </div>
      )}


      {stats && Object.keys(stats.events_by_type).length > 0 && (
        <div className="card" style={{ marginBottom: 24, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>По типам событий</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {Object.entries(stats.events_by_type).map(([type, count]) => {
              const e = EVENT_LABELS[type] || { icon: '📌', label: type };
              return (
                <div key={type} style={{ padding: '6px 12px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', fontSize: 12 }}>
                  {e.icon} {e.label}: <strong>{count}</strong>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {stats && stats.top_products?.length > 0 && (
        <div className="card" style={{ marginBottom: 24, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 12 }}>Топ просматриваемых товаров</div>
          <table className="table" style={{ fontSize: 12 }}>
            <thead><tr><th>ID</th><th>Название</th><th>Просмотров</th></tr></thead>
            <tbody>
              {stats.top_products.map((p, i) => (
                <tr key={i}>
                  <td style={{ fontFamily: 'var(--font-mono)' }}>{p.product_id}</td>
                  <td>{p.product_name || '—'}</td>
                  <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{p.views}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}


      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <Filter size={16} style={{ color: 'var(--text-muted)' }} />
        <select className="form-input" style={{ width: 200 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Все события</option>
          {EVENT_TYPES.filter(Boolean).map(t => (
            <option key={t} value={t}>{EVENT_LABELS[t]?.label || t}</option>
          ))}
        </select>
        <select className="form-input" style={{ width: 140 }} value={days} onChange={e => setDays(parseInt(e.target.value))}>
          <option value={7}>7 дней</option>
          <option value={14}>14 дней</option>
          <option value={30}>30 дней</option>
          <option value={90}>90 дней</option>
        </select>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Всего: {total}</span>
      </div>


      <div className="table-container" style={{ overflowX: 'auto' }}>
        <table className="table" style={{ minWidth: 800, fontSize: 12 }}>
          <thead>
            <tr><th>Время</th><th>Событие</th><th>Страница</th><th>Товар</th><th>Цена</th><th>Клиент</th></tr>
          </thead>
          <tbody>
            {events.map(e => (
              <tr key={e.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{new Date(e.event_time + 'Z').toLocaleString('ru')}</td>
                <td>{getEventLabel(e.event_type)}</td>
                <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={e.page_url}>{e.page_title || e.page_url || '—'}</td>
                <td>{e.product_name || e.product_id || '—'}</td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{e.product_price || e.cart_total ? `${(e.product_price || e.cart_total).toLocaleString('ru')} ₽` : '—'}</td>
                <td style={{ fontSize: 11 }}>
                  {e.client_phone ? (
                    <span style={{ fontFamily: 'var(--font-mono)' }}>{e.client_phone}</span>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 10 }}>{e.visitor_id?.slice(0, 8) || '—'}</span>
                  )}
                </td>
              </tr>
            ))}
            {events.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                {total === 0 ? 'Событий пока нет. Установите пиксель на сайт.' : 'Нет событий по фильтру'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={Math.ceil(total / PER_PAGE)} onPageChange={setPage} />
    </div>
  );
}